import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { CircleLoader } from '@/app/common/components/CircleLoader'
import { Switch } from '@/shad/ui/switch'
import { useUpdateProgramPurse } from '@/queries/purse_configs/useUpdateProgramPurse.ts'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { useQueryClient } from '@tanstack/react-query'

type AutoDistributionPurseData = {
  id: number
  programId: number
  canDistributeAutomatically: boolean
}

type Props = {
  partialPurseData: AutoDistributionPurseData
}

export default function AutoDistributionCard({ partialPurseData }: Props) {
  const updateMutation = useUpdateProgramPurse()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const handleUpdate = async (checked: boolean) => {
    updateMutation.mutate(
      {
        params: {
          program_id: partialPurseData.programId,
          purse_id: partialPurseData.id,
        },
        payload: { can_distribute_funds_automatically: checked },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          toast({
            title: 'Success',
            description: 'Purse distribution setting updated successfully',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description:
              error.message ||
              'Failed to update automatic distribution setting',
          })
        },
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Automatic Distribution
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Enable automatic fund distribution for this purse.
            <br />
            <b>
              Disabling this will require manual distribution of scholarship
              funds.
            </b>
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-2">
          <Switch
            aria-label="Can distribute funds automatically"
            disabled={updateMutation.status === 'pending'}
            checked={partialPurseData.canDistributeAutomatically}
            onCheckedChange={handleUpdate}
          />
          <div className="ml-2 flex items-center text-sm font-medium text-gray-800">
            Can distribute funds automatically
            {updateMutation.status === 'pending' && (
              <span className="ml-2">
                <CircleLoader className="fill-text-950" size="sm" />
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
